import { useAtomValue } from 'jotai';

import InputField from '@rewardopl/react-ui/input_field';
import SelectField from '@rewardopl/react-ui/select_field';
import TextareaField from '@rewardopl/react-ui/textarea_field';

import { toNaturalSentence } from '@rewardopl/utils/string';

import AddressField from './address_field';
import AddressFieldWithDefault from './address_field_with_default';
import CheckboxFieldWithFalsy from './checkbox_field_with_falsy';
import LocationField from './location_field';

import { currentAccountState } from '../../store';

import type { FieldsOrFieldsGetter } from '../../types';

type RenderFieldsArgs<T extends object> = {
  fields: FieldsOrFieldsGetter<T>;
  formData: Partial<T>;
  method?: 'GET' | 'POST' | 'PATCH';
  setFormData: (data: Partial<T>) => void;
};

export default function renderFields<T extends object>({
  fields,
  formData,
  method = 'POST',
  setFormData,
}: RenderFieldsArgs<T>) {
  // biome-ignore lint/correctness/useHookAtTopLevel: An internal helper only used in JSX
  const currentAccount = useAtomValue(currentAccountState);
  const resolvedFields =
    typeof fields === 'function' ? fields({ currentAccount, formData, method }) : fields;
  const filteredFields = resolvedFields.filter(Boolean);

  return filteredFields.map((field) => {
    const key = (field.form || '') + field.name;

    const props = {
      formData,
      label: field.label || toNaturalSentence(field.name),
      onChange: (value: unknown) => setFormData({ ...formData, [field.name]: value }),
      setFormData,
      value:
        formData[field.name as keyof T] ??
        // biome-ignore lint/suspicious/noExplicitAny: Too complex to type
        (('defaultValue' in field ? field.defaultValue : undefined) as any),
    };

    switch (field.type) {
      case 'address': {
        return <AddressField key={key} {...field} {...props} />;
      }
      case 'address_with_default': {
        return <AddressFieldWithDefault key={key} {...field} {...props} />;
      }
      case 'checkbox': {
        const { value, ...otherProps } = props;

        return (
          <CheckboxFieldWithFalsy key={key} {...field} {...otherProps} value={value?.toString()} />
        );
      }
      case 'custom': {
        const { customField: CustomField } = field;

        return <CustomField key={key} {...field} {...props} />;
      }
      case 'hidden': {
        const { defaultValue, ...fieldWithoutDefaultValue } = field;
        const { label, ...otherProps } = props;

        return <input key={key} {...fieldWithoutDefaultValue} {...otherProps} />;
      }
      case 'location': {
        return <LocationField key={key} {...field} {...props} />;
      }
      case 'select': {
        return <SelectField key={key} {...field} {...props} />;
      }
      case 'textarea': {
        return <TextareaField key={key} {...field} {...props} />;
      }
      case 'text':
      default: {
        return <InputField key={key} {...field} {...props} />;
      }
    }
  });
}
